import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  FileText,
  Inbox,
  Pencil,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  createSystemAnalyticsComment,
  deleteSystemAnalyticsComment,
  getSystemAnalyticsComments,
  getSystemAnalyticsSummary,
  updateSystemAnalyticsComment,
} from '../Services/SystemAnalyticsService';
import {
  SYSTEM_ANALYTICS_TOPIC_LABELS,
  SystemAnalyticsCommentDTO,
  SystemAnalyticsSummaryDTO,
  SystemAnalyticsTopic,
} from '../Types/SystemAnalyticsType';
import { formatDateTime } from '../../Shared/Utils/HelperUtils';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import AdminListComponent from './SmartAnalytics/AdminListComponent';

const PAGE_SIZE = 10;

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';
const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const MiniStat = ({
  label,
  value,
  sub,
  icon,
  tone = 'slate',
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone?: 'slate' | 'cyan' | 'emerald';
}) => {
  const valueTone = {
    slate: 'text-slate-100',
    cyan: 'text-cyan-400',
    emerald: 'text-emerald-400',
  }[tone];

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
      <span className="flex items-center gap-2 text-xs font-semibold text-slate-400">
        {icon}
        {label}
      </span>
      <p className={`mt-1.5 text-lg font-black ${valueTone}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-[10px] font-semibold text-slate-500">{sub}</p>}
    </div>
  );
};

const EmptyState = ({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
      {icon}
    </div>
    <p className="text-sm font-black text-slate-200">{title}</p>
    <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">{body}</p>
  </div>
);

const LoadingRows = ({ rows, cols }: { rows: number; cols: number }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex} className="border-t border-slate-800/70">
        {Array.from({ length: cols }).map((__, colIndex) => (
          <td key={colIndex} className="px-5 py-4">
            <div className="h-4 w-full animate-pulse rounded bg-slate-800/80" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/**
 * An unknown topic from a newer backend must still render: a note that was already written is real
 * data and hiding it would be worse than labelling it imperfectly.
 */
const topicLabel = (topic: string) =>
  SYSTEM_ANALYTICS_TOPIC_LABELS[topic as SystemAnalyticsTopic] ??
  SYSTEM_ANALYTICS_TOPIC_LABELS[SystemAnalyticsTopic.OTHER];

const roleBadge = (role: string) => {
  const superAdmin = role?.toUpperCase() === 'SUPER_ADMIN';
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
        superAdmin
          ? 'border border-amber-500/30 bg-amber-500/10 text-amber-400'
          : 'border border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
      }`}
    >
      {superAdmin ? 'Super admin' : 'Delegated'}
    </span>
  );
};

const SystemAnalyticsComponent = () => {
  const [summary, setSummary] = useState<SystemAnalyticsSummaryDTO | null>(null);
  const [notes, setNotes] = useState<PaginationResponse<SystemAnalyticsCommentDTO> | null>(null);

  const [noteSearch, setNoteSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [notePage, setNotePage] = useState(0);

  const [loadingNotes, setLoadingNotes] = useState(true);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [adminRefreshKey, setAdminRefreshKey] = useState(0);

  // Draft state for the composer. Kept as one object so a topic change and a text edit cannot land
  // out of step with each other on save.
  const [draft, setDraft] = useState({ body: '', topic: SystemAnalyticsTopic.CREDIT_EXHAUSTION });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Inline delete confirmation. A second click on the same row is required, not a modal. */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const showError = useCallback((message: string) => {
    setAlert({ variant: 'error', message });
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const response = await getSystemAnalyticsSummary();
      if (response.success) {
        setSummary(response.data);
      }
    } catch {
      // A failed header count should not block the notes below it; the tables still render.
    }
  }, []);

  const loadNotes = useCallback(async () => {
    const response = await getSystemAnalyticsComments(noteSearch, topic, notePage, PAGE_SIZE);
    if (response.success) {
      setNotes(response.data);
    } else {
      showError(response.message);
    }
  }, [noteSearch, topic, notePage, showError]);

  /**
   * Separate effects per data set, so paging the notes does not also refetch the roster and reset
   * both tables to a skeleton. One shared effect looked equivalent but made every page click cost
   * three requests and flashed an unrelated table.
   */
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    let cancelled = false;
    setLoadingNotes(true);
    loadNotes().finally(() => {
      if (!cancelled) setLoadingNotes(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadNotes]);

  /**
   * A new search must return to page 0. Filtering while sitting on page 7 is the classic way to land
   * on an empty page that looks like the data is gone.
   */
  const applyNoteSearch = (value: string) => {
    setNoteSearch(value);
    setNotePage(0);
  };

  const resetComposer = () => {
    setDraft({ body: '', topic: SystemAnalyticsTopic.CREDIT_EXHAUSTION });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!draft.body.trim()) {
      showError('Write something before saving');
      return;
    }

    setSaving(true);
    try {
      const payload = { body: draft.body, topic: draft.topic };
      const response = editingId
        ? await updateSystemAnalyticsComment(editingId, payload)
        : await createSystemAnalyticsComment(payload);

      if (!response.success) {
        showError(response.message);
        return;
      }

      resetComposer();
      setAlert({ variant: 'success', message: editingId ? 'Note updated' : 'Note added' });
      setAdminRefreshKey((k) => k + 1);
      await Promise.all([loadSummary(), loadNotes()]);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    setConfirmDeleteId(null);

    const response = await deleteSystemAnalyticsComment(commentId);
    if (!response.success) {
      showError(response.message);
      return;
    }

    // The deleted note was the row being edited; drop the editor rather than leaving it pointed at
    // something the user can no longer save.
    if (editingId === commentId) {
      resetComposer();
    }
    setAlert({
      variant: 'success',
      message: response.data?.deleted === false ? 'Note was already deleted' : 'Note deleted',
    });
    setAdminRefreshKey((k) => k + 1);
    await Promise.all([loadSummary(), loadNotes()]);
  };

  const beginEdit = (note: SystemAnalyticsCommentDTO) => {
    setConfirmDeleteId(null);
    setEditingId(note.id);
    setDraft({
      body: note.body,
      topic: (note.topic as SystemAnalyticsTopic) || SystemAnalyticsTopic.OTHER,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6">
      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}

      <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-black text-slate-100">
              <Activity className="h-5 w-5 text-cyan-400" />
              System Analytics
            </h1>
            <p className="mt-1 max-w-2xl text-xs font-semibold leading-relaxed text-slate-500">
              What the admin team concluded while watching the numbers. Notes stand on their own and
              are visible only to super admins.
            </p>
          </div>
          <span className="flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-cyan-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Super admin only
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MiniStat
            label="Notes"
            value={summary?.totalNotes ?? 0}
            sub={`${summary?.notesLast7Days ?? 0} in the last 7 days`}
            icon={<FileText className="h-4 w-4" />}
            tone="cyan"
          />
          <MiniStat
            label="All admins"
            value={summary?.totalAdmins ?? 0}
            sub="Candidates and recruiters excluded"
            icon={<Users className="h-4 w-4" />}
          />
          <MiniStat
            label="Super admins"
            value={summary?.superAdmins ?? 0}
            sub={`${summary?.delegatedAdmins ?? 0} delegated`}
            icon={<ShieldCheck className="h-4 w-4" />}
            tone="emerald"
          />
          <MiniStat
            label="Contributors"
            value={summary?.activeContributors ?? 0}
            sub="Admins who have written a note"
            icon={<Pencil className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
            <Send className="h-4 w-4 text-cyan-400" />
            {editingId ? 'Edit note' : 'Add a note'}
          </h2>
          {editingId && (
            <button
              type="button"
              onClick={resetComposer}
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          )}
        </div>

        <textarea
          value={draft.body}
          onChange={(event) => setDraft((prev) => ({ ...prev, body: event.target.value }))}
          rows={4}
          maxLength={10000}
          placeholder="What did the numbers tell you? e.g. trial-only companies exhaust invites faster than paid ones"
          className={`${fieldClass} mt-4 resize-y font-medium leading-relaxed`}
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {Object.values(SystemAnalyticsTopic).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setDraft((prev) => ({ ...prev, topic: option }))}
                className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition ${
                  draft.topic === option
                    ? 'border border-cyan-500/40 bg-cyan-500/15 text-cyan-300'
                    : 'border border-slate-800 bg-slate-950/60 text-slate-500 hover:border-slate-700 hover:text-slate-300'
                }`}
              >
                {SYSTEM_ANALYTICS_TOPIC_LABELS[option]}
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={saving || !draft.body.trim()}
            onClick={handleSubmit}
            className="rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? 'Saving' : editingId ? 'Save changes' : 'Add note'}
          </button>
        </div>
      </div>

      {/* Notes */}
      <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
        <div className="flex flex-wrap items-center justify-between gap-3 p-6">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
              <Inbox className="h-4 w-4 text-cyan-400" />
              Notes
            </h2>
            <p className="mt-1 text-[10px] font-semibold text-slate-500">
              {notes?.totalElements ?? 0} in total, newest first
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
              <input
                value={noteSearch}
                onChange={(event) => applyNoteSearch(event.target.value)}
                placeholder="Search notes or authors"
                className={`${fieldClass} w-64 pl-9`}
              />
            </div>
            <select
              value={topic}
              onChange={(event) => {
                setTopic(event.target.value);
                setNotePage(0);
              }}
              className={`${fieldClass} w-44`}
            >
              <option value="">All topics</option>
              {Object.values(SystemAnalyticsTopic).map((option) => (
                <option key={option} value={option}>
                  {SYSTEM_ANALYTICS_TOPIC_LABELS[option]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <table className="w-full">
          <thead className="border-y border-slate-800 bg-slate-900/60">
            <tr>
              <th className={`${headerClass} text-left`}>Note</th>
              <th className={`${headerClass} text-left`}>Topic</th>
              <th className={`${headerClass} text-left`}>Author</th>
              <th className={`${headerClass} text-left`}>Written</th>
              <th className={`${headerClass} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingNotes ? (
              <LoadingRows rows={4} cols={5} />
            ) : !notes || notes.content.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    icon={<Inbox className="h-6 w-6" />}
                    title="No notes yet"
                    body="Nothing has been recorded here. The first note is usually the most valuable one, because it is what the numbers cannot tell you."
                  />
                </td>
              </tr>
            ) : (
              notes.content.map((note) => (
                <tr key={note.id} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                  <td className="px-5 py-4">
                    <p className="max-w-2xl whitespace-pre-wrap text-xs font-semibold leading-relaxed text-slate-200">
                      {note.body}
                    </p>
                    {note.edited && (
                      <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400">
                        <Pencil className="h-3 w-3" />
                        Edited after writing
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="rounded-full border border-slate-800 bg-slate-950/60 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                      {topicLabel(note.topic)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-xs font-bold text-slate-200">{note.authorName}</p>
                    {roleBadge(note.authorRole)}
                  </td>
                  <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                    {formatDateTime(note.createdAt ?? undefined)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => beginEdit(note)}
                        title="Edit note"
                        className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {confirmDeleteId === note.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleDelete(note.id)}
                            className="rounded-lg border border-rose-500/40 bg-rose-500/15 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(note.id)}
                          title="Delete note"
                          className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {notes && notes.totalPages > 1 && (
          <Pagination {...notes} setPage={setNotePage} />
        )}
      </div>

      <AdminListComponent refreshKey={adminRefreshKey} />
    </div>
  );
};

export default SystemAnalyticsComponent;